import type { Lang } from "./index";

// In-app screen copy in all three shipped languages. Each language is
// written natively (grammar, idiom, tone) — not a literal translation.
// Plural forms and value interpolation are handled via function fields.

export type AppCopy = {
  // === shared / primitives ===
  backJournal: string;
  confHigh: string;
  confMid: string;
  confLow: string;
  confIndicatorLow: string;
  confAria: (pct: number) => string;
  aiFooterDefault: string;

  // === bottom nav + add sheet ===
  navHome: string;
  navFridge: string;
  navBulb: string;
  navProfile: string;
  addMealAria: string;
  closeAria: string;
  addSheetTitle: string;
  addMethodTextLabel: string;
  addMethodTextSub: string;
  addMethodPhotoLabel: string;
  addMethodPhotoSub: string;
  addMethodVoiceLabel: string;
  addMethodVoiceSub: string;
  addMethodBarcodeLabel: string;
  addMethodBarcodeSub: string;
  addMethodMenuLabel: string;
  addMethodMenuSub: string;
  addFromFavorites: string;

  // === dashboard (household) ===
  dashTopTitle: string;
  dashSignOut: string;
  dashLoading: string;
  dashYourHome: string;
  dashMemberCount: (n: number) => string;
  dashSharedTagline: string;
  dashVerifyEmail: (email: string) => string;
  dashVerifyResending: string;
  dashVerifyResend: string;
  dashVerifySent: string;
  dashLoadFailed: string;
  dashSendFailed: string;
  dashMembersEyebrow: string;
  dashMembersLoading: string;
  dashMembersEmpty: string;
  dashYouSuffix: string;
  dashAdminBadge: string;
  dashJoinedPrefix: string;
  dashRelToday: string;
  dashRelYesterday: string;
  dashRelDays: (n: number) => string;
  dashRelMonths: (n: number) => string;
  dashRelYears: (n: number) => string;
  dashInviteEyebrow: string;
  dashInviteCopied: string;
  dashInviteCopy: string;
  dashInviteCodePrefix: string;
  dashInviteHint: string;
  dashInviteGenerating: string;
  dashInviteGenerate: string;
  dashInviteFailed: string;
  dashAdminEyebrow: string;
  dashAdminPanel: string;
  dashAdminPanelSub: string;
  dashYourDayEyebrow: string;
  dashCalorieGoal: string;
  dashCalorieGoalSub: string;
  dashLanguageEyebrow: string;
  dashSharedDataEyebrow: string;
  dashSharedFridge: string;
  dashSharedFridgeHint: string;
  dashSharedList: string;
  dashSharedListHint: string;
  dashSharedMeals: string;
  dashSharedMealsHint: string;
  dashGdprEyebrow: string;
  dashGdprBody: string;
  dashExporting: string;
  dashExport: string;
  dashExportFailed: string;
  dashDeleting: string;
  dashDeleteAccount: string;
  dashDeleteFailed: string;
  dashDeletePrompt: string;
  dashDeleteConfirmWord: string;
  dashFooterPrivacy: string;
  dashFooterTerms: string;

  // === targets screen ===
  targetsTopTitle: string;
  targetsBack: string;
  targetsHeading: string;
  targetsIntro: string;
  targetsKcalEyebrow: string;
  targetsDone: string;
  targetsEdit: string;
  targetsSuggestedTdee: (kcal: number) => string;
  targetsTdeeMethod: string;
  targetsUse: string;
  targetsEstimateDisclaimer: string;
  targetsMacroEyebrow: string;
  targetsMacroOk: string;
  targetsMacroBad: (sum: number) => string;
  targetsMacroProtein: string;
  targetsMacroFat: string;
  targetsMacroCarbs: string;
  targetsAutoTdee: string;
  targetsAutoTdeeValue: (kcal: number) => string;
  targetsAutoTdeeHint: string;
  targetsDemographicsNote: string;
  targetsSexLabel: string;
  targetsSexNone: string;
  targetsSexFemale: string;
  targetsSexMale: string;
  targetsBirthYearLabel: string;
  targetsBirthYearPlaceholder: string;
  targetsHeightLabel: string;
  targetsHeightPlaceholder: string;
  targetsWeightLabel: string;
  targetsWeightPlaceholder: string;
  targetsActivityLabel: string;
  targetsActivityNone: string;
  targetsActivitySedentary: string;
  targetsActivityLight: string;
  targetsActivityModerate: string;
  targetsActivityHigh: string;
  targetsActivityVeryHigh: string;
  targetsUseTdee: (kcal: number) => string;
  targetsFillAll: string;
  targetsSafetyEyebrow: string;
  targetsSafetyBody: string;
  targetsPctSumError: (sum: number) => string;
  targetsGenericError: string;
  targetsSaving: string;
  targetsSave: string;
  targetsCancel: string;
  targetsSignOut: string;

  // === low-target sanity modal ===
  sanityTitle: string;
  sanityBody: (kcal: number) => string;
  sanitySupport: string;
  sanityConfirm: string;
  sanityCancel: string;

  // === crisis support footer ===
  crisisSupport: string;
  // Crisis helpline shown in the footer + low-target modal. Per-country:
  // PL emotional-support line, DE Telefonseelsorge, EN international directory.
  helplineLabel: string;
  helplineHref: string;

  // === favorites screen ===
  favTopTitle: string;
  favBack: string;
  favEdit: string;
  favDone: string;
  favHeading: string;
  favCount: (n: number) => string;
  favTagline: string;
  favLoading: string;
  favSearchPlaceholder: string;
  favSortUseCount: string;
  favSortLastUsed: string;
  favSortAlphabetical: string;
  favEmptyState: string;
  favNoMatch: (query: string) => string;
  favRelNever: string;
  favRelToday: string;
  favRelYesterday: string;
  favRelDays: (n: number) => string;
  favRelWeekAgo: string;
  favRelWeeks: (n: number) => string;
  favRelMonths: (n: number) => string;
  favDeleteConfirm: (name: string) => string;
  favDelete: string;
  favLog: string;
  favDeleteAria: (name: string) => string;
  favLoadFailed: string;
  favLogFailed: string;
  favDeleteFailed: string;

  // === favorites section ===
  favSectionHeading: string;
  favSectionSeeAll: (n: number) => string;
  favSectionManage: string;
  favSectionAdd: string;
  favSectionDeleteAria: (name: string) => string;

  // === favorites strip ===
  favStripEyebrow: string;

  // === admin screen ===
  adminBack: string;
  adminEyebrow: string;
  adminHeading: string;
  adminNoAccess: string;
  adminLoadFailed: string;
  adminLoading: string;
  adminKpiUsers: string;
  adminKpiUsersSub: (verified: number) => string;
  adminKpiActive7d: string;
  adminKpiCostMonth: string;
  adminKpiCostMonthSub: (calls: number) => string;
  adminKpiCostTotal: string;
  adminKpiCostTotalSub: (alerts: number) => string;
  adminKpiReferrals: string;
  adminKpiReferralsSub: (active: number) => string;
  adminCostByModel: string;
  adminNoData: string;
  adminCalls: string;
  adminTopReferrers: string;
  adminTopSpenders: string;
  adminAllUsers: (n: number) => string;
  adminSearchPlaceholder: string;
  adminSortCost: string;
  adminSortCalls: string;
  adminSortInvites: string;
  adminSortCreated: string;
  adminSortActive: string;
  adminUnverifiedBadge: string;
  adminUserMetaLast: string;
  adminUserMetaCode: string;
  adminUserMetaInvites: string;
  adminUserMetaFrom: string;
  adminPerMonth: string;
  adminCloseAria: string;
  adminDetailCalls: string;
  adminDetailTotal: string;
  adminDetailMonth: string;
  adminDetailReferral: string;
  adminDetailOwnCode: string;
  adminDetailNoCode: string;
  adminDetailInvitedBy: string;
  adminDetailRecentCalls: string;
  adminDetailNoCalls: string;
  adminDetailClose: string;
};

const pl: AppCopy = {
  backJournal: "Dziennik",
  confHigh: "pewne",
  confMid: "sprawdź",
  confLow: "niepewne",
  confIndicatorLow: "popraw",
  confAria: (pct) => `pewność ${pct}%`,
  aiFooterDefault: "Wartości szacunkowe. Sprawdź przed zapisem.",

  navHome: "Dziennik",
  navFridge: "Lodówka",
  navBulb: "Pomysł",
  navProfile: "Profil",
  addMealAria: "Dodaj posiłek",
  closeAria: "Zamknij",
  addSheetTitle: "Dodaj posiłek",
  addMethodTextLabel: "Wpisz",
  addMethodTextSub: "Tekst → AI",
  addMethodPhotoLabel: "Zdjęcie",
  addMethodPhotoSub: "Talerz → makro",
  addMethodVoiceLabel: "Mów",
  addMethodVoiceSub: "Powiedz co zjadłeś",
  addMethodBarcodeLabel: "Kod",
  addMethodBarcodeSub: "EAN",
  addMethodMenuLabel: "Menu",
  addMethodMenuSub: "Restauracja",
  addFromFavorites: "albo z ulubionych",

  dashTopTitle: "HOUSEHOLD",
  dashSignOut: "Wyloguj",
  dashLoading: "Ładuję",
  dashYourHome: "Twój dom",
  dashMemberCount: (n) => `${n} ${n === 1 ? "osoba" : n < 5 ? "osoby" : "osób"}`,
  dashSharedTagline: "współdzielicie lodówkę i listę",
  dashVerifyEmail: (email) => `Potwierdź email. Wysłaliśmy link na ${email}.`,
  dashVerifyResending: "Wysyłam…",
  dashVerifyResend: "Wyślij ponownie",
  dashVerifySent: "Wysłaliśmy link weryfikacyjny",
  dashLoadFailed: "Nie udało się załadować",
  dashSendFailed: "Nie udało się wysłać",
  dashMembersEyebrow: "Członkowie",
  dashMembersLoading: "Ładuję…",
  dashMembersEmpty: "Tylko Ty na razie. Zaproś rodzinę poniżej.",
  dashYouSuffix: " (Ty)",
  dashAdminBadge: "ADMIN",
  dashJoinedPrefix: "DOŁĄCZONO · ",
  dashRelToday: "dziś",
  dashRelYesterday: "wczoraj",
  dashRelDays: (n) => `${n} dni temu`,
  dashRelMonths: (n) => `${n} mies. temu`,
  dashRelYears: (n) => `${n} lat temu`,
  dashInviteEyebrow: "Link zaproszenia",
  dashInviteCopied: "Skopiowano",
  dashInviteCopy: "Kopiuj",
  dashInviteCodePrefix: "KOD · ",
  dashInviteHint: "Wygeneruj kod, wyślij rodzinie. Otworzą link i dołączą.",
  dashInviteGenerating: "Tworzę…",
  dashInviteGenerate: "Generuj zaproszenie",
  dashInviteFailed: "Nie udało się utworzyć zaproszenia",
  dashAdminEyebrow: "Admin",
  dashAdminPanel: "Panel admina",
  dashAdminPanelSub: "USERZY · KOSZTY · STATYSTYKI",
  dashYourDayEyebrow: "Twój dzień",
  dashCalorieGoal: "Cel kaloryczny i makro",
  dashCalorieGoalSub: "EDYTUJ KCAL · BIAŁKO · TŁUSZCZ · WĘGLE",
  dashLanguageEyebrow: "Język · Language · Sprache",
  dashSharedDataEyebrow: "Wspólne dane",
  dashSharedFridge: "Lodówka",
  dashSharedFridgeHint: "widoczna dla wszystkich",
  dashSharedList: "Lista zakupów",
  dashSharedListHint: "wszyscy mogą edytować",
  dashSharedMeals: "Posiłki i cele",
  dashSharedMealsHint: "prywatne",
  dashGdprEyebrow: "Twoje dane · RODO",
  dashGdprBody:
    "Art. 15 + 20: pobierz pełen JSON ze swoimi danymi. Art. 17: usuń konto jednym tapem.",
  dashExporting: "Eksportuję…",
  dashExport: "Eksportuj moje dane (JSON)",
  dashExportFailed: "Eksport nie zadziałał",
  dashDeleting: "Usuwam…",
  dashDeleteAccount: "Usuń konto na zawsze",
  dashDeleteFailed: "Usunięcie nie zadziałało",
  dashDeletePrompt:
    'Usunięcie jest BEZPOWROTNE. Wszystkie posiłki, lodówka, ulubione znikną. Wpisz "USUŃ" żeby potwierdzić:',
  dashDeleteConfirmWord: "USUŃ",
  dashFooterPrivacy: "Polityka prywatności",
  dashFooterTerms: "Regulamin",

  targetsTopTitle: "CEL",
  targetsBack: "Dziennik",
  targetsHeading: "Twój cel",
  targetsIntro: "Bez wagi w łazience. Bez waluty kalorii. Tylko liczby pod Twój dzień.",
  targetsKcalEyebrow: "Kcal dziennie",
  targetsDone: "Gotowe",
  targetsEdit: "Edytuj",
  targetsSuggestedTdee: (kcal) => `Sugerowane TDEE: ${kcal} kcal`,
  targetsTdeeMethod: "(Mifflin-St Jeor)",
  targetsUse: "Użyj",
  targetsEstimateDisclaimer: "Wartości szacunkowe. Nie zastępują porady dietetyka.",
  targetsMacroEyebrow: "Rozkład makro · %",
  targetsMacroOk: "= 100",
  targetsMacroBad: (sum) => `= ${sum} (musi być 100)`,
  targetsMacroProtein: "BIAŁKO",
  targetsMacroFat: "TŁUSZCZ",
  targetsMacroCarbs: "WĘGLE",
  targetsAutoTdee: "Auto-TDEE",
  targetsAutoTdeeValue: (kcal) => `${kcal} kcal · Mifflin-St Jeor`,
  targetsAutoTdeeHint: "4 dane → sugestia kcal",
  targetsDemographicsNote: "Dane opcjonalne, używamy tylko do wzoru. Nie są pokazywane innym.",
  targetsSexLabel: "PŁEĆ BIOLOGICZNA",
  targetsSexNone: "— nie podaję —",
  targetsSexFemale: "kobieta",
  targetsSexMale: "mężczyzna",
  targetsBirthYearLabel: "ROK URODZENIA",
  targetsBirthYearPlaceholder: "np. 1990",
  targetsHeightLabel: "WZROST (CM)",
  targetsHeightPlaceholder: "np. 178",
  targetsWeightLabel: "WAGA (KG)",
  targetsWeightPlaceholder: "np. 72",
  targetsActivityLabel: "POZIOM AKTYWNOŚCI",
  targetsActivityNone: "— wybierz —",
  targetsActivitySedentary: "Siedzący (biuro, mało ruchu)",
  targetsActivityLight: "Lekki (spacer, 1-2× sport/tydzień)",
  targetsActivityModerate: "Średni (3-5× sport/tydzień)",
  targetsActivityHigh: "Wysoki (6-7× sport, fizyczna praca)",
  targetsActivityVeryHigh: "Bardzo wysoki (codzienny ciężki trening)",
  targetsUseTdee: (kcal) => `Użyj ${kcal} kcal`,
  targetsFillAll: "Wypełnij wszystkie pola żeby zobaczyć szacunek.",
  targetsSafetyEyebrow: "BEZPIECZEŃSTWO",
  targetsSafetyBody: "Cel < 1 200 kcal wymaga potwierdzenia. Wsparcie: 116 123.",
  targetsPctSumError: (sum) => `Procenty muszą sumować się do 100 (jest ${sum})`,
  targetsGenericError: "Coś nie wyszło",
  targetsSaving: "Zapisuję…",
  targetsSave: "Zapisz cel",
  targetsCancel: "Anuluj",
  targetsSignOut: "Wyloguj się",

  sanityTitle: "Sprawdźmy razem",
  sanityBody: (kcal) =>
    `${kcal} kcal dziennie to mniej niż minimum medyczne dla większości dorosłych. Jeśli ten cel ustalił z Tobą dietetyk, OK. Jeśli to Twój pomysł, warto skonsultować.`,
  sanitySupport:
    "Jeśli zmagasz się z myślami o jedzeniu lub wadze, porozmawiaj z kimś. Telefon Zaufania:",
  sanityConfirm: "Wiem co robię",
  sanityCancel: "Wróć i popraw",

  crisisSupport:
    "Jeśli zmagasz się z myślami o jedzeniu lub wadze, porozmawiaj z kimś. Telefon Zaufania:",
  helplineLabel: "116 123",
  helplineHref: "tel:116123",

  favTopTitle: "ULUBIONE",
  favBack: "Dziennik",
  favEdit: "Edytuj",
  favDone: "Gotowe",
  favHeading: "Ulubione",
  favCount: (n) => `${n} ${n === 1 ? "pozycja" : "pozycji"}`,
  favTagline: "zaloguj w sekundę",
  favLoading: "Ładuję…",
  favSearchPlaceholder: "Szukaj…",
  favSortUseCount: "NAJCZĘŚCIEJ",
  favSortLastUsed: "OSTATNIO",
  favSortAlphabetical: "A–Z",
  favEmptyState: 'Pusto. Dodaj ulubione tapem „☆ ulubione" przy posiłku na trackerze.',
  favNoMatch: (query) => `Nic nie pasuje do „${query}".`,
  favRelNever: "NIE UŻYTE",
  favRelToday: "DZIŚ",
  favRelYesterday: "WCZORAJ",
  favRelDays: (n) => `${n} DNI TEMU`,
  favRelWeekAgo: "TYDZIEŃ TEMU",
  favRelWeeks: (n) => `${n} TYG. TEMU`,
  favRelMonths: (n) => `${n} MIES. TEMU`,
  favDeleteConfirm: (name) => `Usunąć "${name}" z ulubionych?`,
  favDelete: "Usuń",
  favLog: "+ Zaloguj",
  favDeleteAria: (name) => `usuń ${name}`,
  favLoadFailed: "Nie udało się załadować",
  favLogFailed: "Nie udało się dodać",
  favDeleteFailed: "Nie udało się usunąć",

  favSectionHeading: "Ulubione",
  favSectionSeeAll: (n) => `Wszystkie (${n})`,
  favSectionManage: "Zarządzaj",
  favSectionAdd: "Dodaj",
  favSectionDeleteAria: (name) => `usuń z ulubionych ${name}`,

  favStripEyebrow: "★ Szybki log",

  adminBack: "Wróć",
  adminEyebrow: "PANEL ADMINA",
  adminHeading: "Sytofood admin",
  adminNoAccess: "Brak dostępu",
  adminLoadFailed: "Nie udało się załadować",
  adminLoading: "Ładuję...",
  adminKpiUsers: "USERZY",
  adminKpiUsersSub: (verified) => `${verified} verified`,
  adminKpiActive7d: "AKTYWNI 7D",
  adminKpiCostMonth: "KOSZT MIESIĄCA",
  adminKpiCostMonthSub: (calls) => `${calls} call total`,
  adminKpiCostTotal: "KOSZT CAŁOŚCIOWY",
  adminKpiCostTotalSub: (alerts) => `alertów: ${alerts} (30d)`,
  adminKpiReferrals: "REFERRALE",
  adminKpiReferralsSub: (active) => `${active} aktywnych`,
  adminCostByModel: "KOSZT WG MODELU",
  adminNoData: "Brak danych.",
  adminCalls: "CALLS",
  adminTopReferrers: "TOP REFERRERZY",
  adminTopSpenders: "TOP SPENDERZY (TEN MIESIĄC)",
  adminAllUsers: (n) => `WSZYSCY UŻYTKOWNICY (${n})`,
  adminSearchPlaceholder: "Szukaj email / imię...",
  adminSortCost: "Koszt",
  adminSortCalls: "Calls",
  adminSortInvites: "Zaproszeni",
  adminSortCreated: "Założeni",
  adminSortActive: "Aktywni",
  adminUnverifiedBadge: "⚠ NIEWERYFIK.",
  adminUserMetaLast: "OST.",
  adminUserMetaCode: "KOD",
  adminUserMetaInvites: "ZAPROSZ.",
  adminUserMetaFrom: "OD",
  adminPerMonth: "/MO",
  adminCloseAria: "Zamknij",
  adminDetailCalls: "CALLS",
  adminDetailTotal: "OGÓŁEM",
  adminDetailMonth: "MIESIĄC",
  adminDetailReferral: "REFERRAL",
  adminDetailOwnCode: "Kod własny",
  adminDetailNoCode: "brak (legacy)",
  adminDetailInvitedBy: "Zaproszony przez",
  adminDetailRecentCalls: "OSTATNIE 100 CALLI",
  adminDetailNoCalls: "Brak calli.",
  adminDetailClose: "Zamknij",
};

const en: AppCopy = {
  backJournal: "Journal",
  confHigh: "sure",
  confMid: "check",
  confLow: "unsure",
  confIndicatorLow: "fix",
  confAria: (pct) => `confidence ${pct}%`,
  aiFooterDefault: "Estimated values. Check before saving.",

  navHome: "Journal",
  navFridge: "Fridge",
  navBulb: "Idea",
  navProfile: "Profile",
  addMealAria: "Add a meal",
  closeAria: "Close",
  addSheetTitle: "Add a meal",
  addMethodTextLabel: "Type",
  addMethodTextSub: "Text → AI",
  addMethodPhotoLabel: "Photo",
  addMethodPhotoSub: "Plate → macros",
  addMethodVoiceLabel: "Speak",
  addMethodVoiceSub: "Say what you ate",
  addMethodBarcodeLabel: "Barcode",
  addMethodBarcodeSub: "EAN",
  addMethodMenuLabel: "Menu",
  addMethodMenuSub: "Restaurant",
  addFromFavorites: "or from favourites",

  dashTopTitle: "HOUSEHOLD",
  dashSignOut: "Sign out",
  dashLoading: "Loading",
  dashYourHome: "Your home",
  dashMemberCount: (n) => `${n} ${n === 1 ? "person" : "people"}`,
  dashSharedTagline: "you share a fridge and a list",
  dashVerifyEmail: (email) => `Verify your email. We sent a link to ${email}.`,
  dashVerifyResending: "Sending…",
  dashVerifyResend: "Resend",
  dashVerifySent: "We sent a verification link",
  dashLoadFailed: "Could not load",
  dashSendFailed: "Could not send",
  dashMembersEyebrow: "Members",
  dashMembersLoading: "Loading…",
  dashMembersEmpty: "Just you for now. Invite your family below.",
  dashYouSuffix: " (you)",
  dashAdminBadge: "ADMIN",
  dashJoinedPrefix: "JOINED · ",
  dashRelToday: "today",
  dashRelYesterday: "yesterday",
  dashRelDays: (n) => `${n} ${n === 1 ? "day" : "days"} ago`,
  dashRelMonths: (n) => `${n} ${n === 1 ? "month" : "months"} ago`,
  dashRelYears: (n) => `${n} ${n === 1 ? "year" : "years"} ago`,
  dashInviteEyebrow: "Invite link",
  dashInviteCopied: "Copied",
  dashInviteCopy: "Copy",
  dashInviteCodePrefix: "CODE · ",
  dashInviteHint: "Generate a code, send it to your family. They open the link and join.",
  dashInviteGenerating: "Creating…",
  dashInviteGenerate: "Generate invite",
  dashInviteFailed: "Could not create the invite",
  dashAdminEyebrow: "Admin",
  dashAdminPanel: "Admin panel",
  dashAdminPanelSub: "USERS · COSTS · STATS",
  dashYourDayEyebrow: "Your day",
  dashCalorieGoal: "Calorie goal and macros",
  dashCalorieGoalSub: "EDIT KCAL · PROTEIN · FAT · CARBS",
  dashLanguageEyebrow: "Język · Language · Sprache",
  dashSharedDataEyebrow: "Shared data",
  dashSharedFridge: "Fridge",
  dashSharedFridgeHint: "visible to everyone",
  dashSharedList: "Shopping list",
  dashSharedListHint: "everyone can edit",
  dashSharedMeals: "Meals and goals",
  dashSharedMealsHint: "private",
  dashGdprEyebrow: "Your data · GDPR",
  dashGdprBody:
    "Art. 15 + 20: download a full JSON of your data. Art. 17: delete your account in one tap.",
  dashExporting: "Exporting…",
  dashExport: "Export my data (JSON)",
  dashExportFailed: "Export did not work",
  dashDeleting: "Deleting…",
  dashDeleteAccount: "Delete account forever",
  dashDeleteFailed: "Deletion did not work",
  dashDeletePrompt:
    'Deletion is PERMANENT. All meals, fridge and favourites will be gone. Type "DELETE" to confirm:',
  dashDeleteConfirmWord: "DELETE",
  dashFooterPrivacy: "Privacy policy",
  dashFooterTerms: "Terms",

  targetsTopTitle: "GOAL",
  targetsBack: "Journal",
  targetsHeading: "Your goal",
  targetsIntro: "No bathroom scale. No calorie currency. Just numbers for your day.",
  targetsKcalEyebrow: "Kcal per day",
  targetsDone: "Done",
  targetsEdit: "Edit",
  targetsSuggestedTdee: (kcal) => `Suggested TDEE: ${kcal} kcal`,
  targetsTdeeMethod: "(Mifflin-St Jeor)",
  targetsUse: "Use",
  targetsEstimateDisclaimer: "Estimated values. Not a substitute for a dietitian's advice.",
  targetsMacroEyebrow: "Macro split · %",
  targetsMacroOk: "= 100",
  targetsMacroBad: (sum) => `= ${sum} (must be 100)`,
  targetsMacroProtein: "PROTEIN",
  targetsMacroFat: "FAT",
  targetsMacroCarbs: "CARBS",
  targetsAutoTdee: "Auto-TDEE",
  targetsAutoTdeeValue: (kcal) => `${kcal} kcal · Mifflin-St Jeor`,
  targetsAutoTdeeHint: "4 inputs → kcal suggestion",
  targetsDemographicsNote:
    "These fields are optional, used only for the formula. They are not shown to others.",
  targetsSexLabel: "BIOLOGICAL SEX",
  targetsSexNone: "— prefer not to say —",
  targetsSexFemale: "female",
  targetsSexMale: "male",
  targetsBirthYearLabel: "BIRTH YEAR",
  targetsBirthYearPlaceholder: "e.g. 1990",
  targetsHeightLabel: "HEIGHT (CM)",
  targetsHeightPlaceholder: "e.g. 178",
  targetsWeightLabel: "WEIGHT (KG)",
  targetsWeightPlaceholder: "e.g. 72",
  targetsActivityLabel: "ACTIVITY LEVEL",
  targetsActivityNone: "— select —",
  targetsActivitySedentary: "Sedentary (desk job, little movement)",
  targetsActivityLight: "Light (walks, 1-2× sport/week)",
  targetsActivityModerate: "Moderate (3-5× sport/week)",
  targetsActivityHigh: "High (6-7× sport, physical work)",
  targetsActivityVeryHigh: "Very high (daily hard training)",
  targetsUseTdee: (kcal) => `Use ${kcal} kcal`,
  targetsFillAll: "Fill in all fields to see an estimate.",
  targetsSafetyEyebrow: "SAFETY",
  targetsSafetyBody:
    "A goal below 1,200 kcal needs confirmation. Support: findahelpline.com.",
  targetsPctSumError: (sum) => `Percentages must add up to 100 (currently ${sum})`,
  targetsGenericError: "Something went wrong",
  targetsSaving: "Saving…",
  targetsSave: "Save goal",
  targetsCancel: "Cancel",
  targetsSignOut: "Sign out",

  sanityTitle: "Let's check this together",
  sanityBody: (kcal) =>
    `${kcal} kcal a day is below the medical minimum for most adults. If a dietitian set this goal with you, that's fine. If it's your own idea, it's worth a consultation.`,
  sanitySupport:
    "If you're struggling with thoughts about food or weight, talk to someone. Find a helpline near you:",
  sanityConfirm: "I know what I'm doing",
  sanityCancel: "Go back and fix it",

  crisisSupport:
    "If you're struggling with thoughts about food or weight, talk to someone. Find a helpline near you:",
  helplineLabel: "findahelpline.com",
  helplineHref: "https://findahelpline.com",

  favTopTitle: "FAVOURITES",
  favBack: "Journal",
  favEdit: "Edit",
  favDone: "Done",
  favHeading: "Favourites",
  favCount: (n) => `${n} ${n === 1 ? "item" : "items"}`,
  favTagline: "log it in a second",
  favLoading: "Loading…",
  favSearchPlaceholder: "Search…",
  favSortUseCount: "MOST USED",
  favSortLastUsed: "RECENT",
  favSortAlphabetical: "A–Z",
  favEmptyState: 'Nothing here. Add favourites by tapping "☆ favourite" on a meal in the tracker.',
  favNoMatch: (query) => `Nothing matches "${query}".`,
  favRelNever: "NEVER USED",
  favRelToday: "TODAY",
  favRelYesterday: "YESTERDAY",
  favRelDays: (n) => `${n} ${n === 1 ? "DAY" : "DAYS"} AGO`,
  favRelWeekAgo: "A WEEK AGO",
  favRelWeeks: (n) => `${n} ${n === 1 ? "WEEK" : "WEEKS"} AGO`,
  favRelMonths: (n) => `${n} ${n === 1 ? "MONTH" : "MONTHS"} AGO`,
  favDeleteConfirm: (name) => `Remove "${name}" from favourites?`,
  favDelete: "Delete",
  favLog: "+ Log",
  favDeleteAria: (name) => `delete ${name}`,
  favLoadFailed: "Could not load",
  favLogFailed: "Could not add",
  favDeleteFailed: "Could not delete",

  favSectionHeading: "Favourites",
  favSectionSeeAll: (n) => `See all (${n})`,
  favSectionManage: "Manage",
  favSectionAdd: "Add",
  favSectionDeleteAria: (name) => `remove ${name} from favourites`,

  favStripEyebrow: "★ Quick log",

  adminBack: "Back",
  adminEyebrow: "ADMIN PANEL",
  adminHeading: "Sytofood admin",
  adminNoAccess: "No access",
  adminLoadFailed: "Could not load",
  adminLoading: "Loading...",
  adminKpiUsers: "USERS",
  adminKpiUsersSub: (verified) => `${verified} verified`,
  adminKpiActive7d: "ACTIVE 7D",
  adminKpiCostMonth: "MONTH COST",
  adminKpiCostMonthSub: (calls) => `${calls} calls total`,
  adminKpiCostTotal: "TOTAL COST",
  adminKpiCostTotalSub: (alerts) => `alerts: ${alerts} (30d)`,
  adminKpiReferrals: "REFERRALS",
  adminKpiReferralsSub: (active) => `${active} active`,
  adminCostByModel: "COST BY MODEL",
  adminNoData: "No data.",
  adminCalls: "CALLS",
  adminTopReferrers: "TOP REFERRERS",
  adminTopSpenders: "TOP SPENDERS (THIS MONTH)",
  adminAllUsers: (n) => `ALL USERS (${n})`,
  adminSearchPlaceholder: "Search email / name...",
  adminSortCost: "Cost",
  adminSortCalls: "Calls",
  adminSortInvites: "Invited",
  adminSortCreated: "Created",
  adminSortActive: "Active",
  adminUnverifiedBadge: "⚠ UNVERIFIED",
  adminUserMetaLast: "LAST",
  adminUserMetaCode: "CODE",
  adminUserMetaInvites: "INVITED",
  adminUserMetaFrom: "FROM",
  adminPerMonth: "/MO",
  adminCloseAria: "Close",
  adminDetailCalls: "CALLS",
  adminDetailTotal: "TOTAL",
  adminDetailMonth: "MONTH",
  adminDetailReferral: "REFERRAL",
  adminDetailOwnCode: "Own code",
  adminDetailNoCode: "none (legacy)",
  adminDetailInvitedBy: "Invited by",
  adminDetailRecentCalls: "LAST 100 CALLS",
  adminDetailNoCalls: "No calls.",
  adminDetailClose: "Close",
};

const de: AppCopy = {
  backJournal: "Tagebuch",
  confHigh: "sicher",
  confMid: "prüfen",
  confLow: "unsicher",
  confIndicatorLow: "korrigieren",
  confAria: (pct) => `Sicherheit ${pct}%`,
  aiFooterDefault: "Schätzwerte. Vor dem Speichern prüfen.",

  navHome: "Tagebuch",
  navFridge: "Kühlschrank",
  navBulb: "Idee",
  navProfile: "Profil",
  addMealAria: "Mahlzeit hinzufügen",
  closeAria: "Schließen",
  addSheetTitle: "Mahlzeit hinzufügen",
  addMethodTextLabel: "Tippen",
  addMethodTextSub: "Text → KI",
  addMethodPhotoLabel: "Foto",
  addMethodPhotoSub: "Teller → Makros",
  addMethodVoiceLabel: "Sprechen",
  addMethodVoiceSub: "Sag, was du gegessen hast",
  addMethodBarcodeLabel: "Barcode",
  addMethodBarcodeSub: "EAN",
  addMethodMenuLabel: "Karte",
  addMethodMenuSub: "Restaurant",
  addFromFavorites: "oder aus Favoriten",

  dashTopTitle: "HAUSHALT",
  dashSignOut: "Abmelden",
  dashLoading: "Lädt",
  dashYourHome: "Dein Zuhause",
  dashMemberCount: (n) => `${n} ${n === 1 ? "Person" : "Personen"}`,
  dashSharedTagline: "ihr teilt euch Kühlschrank und Liste",
  dashVerifyEmail: (email) => `Bestätige deine E-Mail. Wir haben einen Link an ${email} gesendet.`,
  dashVerifyResending: "Sende…",
  dashVerifyResend: "Erneut senden",
  dashVerifySent: "Wir haben einen Bestätigungslink gesendet",
  dashLoadFailed: "Laden fehlgeschlagen",
  dashSendFailed: "Senden fehlgeschlagen",
  dashMembersEyebrow: "Mitglieder",
  dashMembersLoading: "Lädt…",
  dashMembersEmpty: "Bisher nur du. Lade unten deine Familie ein.",
  dashYouSuffix: " (du)",
  dashAdminBadge: "ADMIN",
  dashJoinedPrefix: "BEIGETRETEN · ",
  dashRelToday: "heute",
  dashRelYesterday: "gestern",
  dashRelDays: (n) => `vor ${n} ${n === 1 ? "Tag" : "Tagen"}`,
  dashRelMonths: (n) => `vor ${n} ${n === 1 ? "Monat" : "Monaten"}`,
  dashRelYears: (n) => `vor ${n} ${n === 1 ? "Jahr" : "Jahren"}`,
  dashInviteEyebrow: "Einladungslink",
  dashInviteCopied: "Kopiert",
  dashInviteCopy: "Kopieren",
  dashInviteCodePrefix: "CODE · ",
  dashInviteHint:
    "Erzeuge einen Code, schick ihn deiner Familie. Sie öffnen den Link und treten bei.",
  dashInviteGenerating: "Erstelle…",
  dashInviteGenerate: "Einladung erzeugen",
  dashInviteFailed: "Einladung konnte nicht erstellt werden",
  dashAdminEyebrow: "Admin",
  dashAdminPanel: "Admin-Panel",
  dashAdminPanelSub: "NUTZER · KOSTEN · STATISTIK",
  dashYourDayEyebrow: "Dein Tag",
  dashCalorieGoal: "Kalorienziel und Makros",
  dashCalorieGoalSub: "KCAL · EIWEISS · FETT · KOHLENHYDRATE BEARBEITEN",
  dashLanguageEyebrow: "Język · Language · Sprache",
  dashSharedDataEyebrow: "Gemeinsame Daten",
  dashSharedFridge: "Kühlschrank",
  dashSharedFridgeHint: "für alle sichtbar",
  dashSharedList: "Einkaufsliste",
  dashSharedListHint: "alle dürfen bearbeiten",
  dashSharedMeals: "Mahlzeiten und Ziele",
  dashSharedMealsHint: "privat",
  dashGdprEyebrow: "Deine Daten · DSGVO",
  dashGdprBody:
    "Art. 15 + 20: Lade ein vollständiges JSON deiner Daten herunter. Art. 17: Lösche dein Konto mit einem Tippen.",
  dashExporting: "Exportiere…",
  dashExport: "Meine Daten exportieren (JSON)",
  dashExportFailed: "Export hat nicht funktioniert",
  dashDeleting: "Lösche…",
  dashDeleteAccount: "Konto endgültig löschen",
  dashDeleteFailed: "Löschen hat nicht funktioniert",
  dashDeletePrompt:
    'Das Löschen ist UNWIDERRUFLICH. Alle Mahlzeiten, der Kühlschrank und die Favoriten sind weg. Tippe "LÖSCHEN" zum Bestätigen:',
  dashDeleteConfirmWord: "LÖSCHEN",
  dashFooterPrivacy: "Datenschutz",
  dashFooterTerms: "AGB",

  targetsTopTitle: "ZIEL",
  targetsBack: "Tagebuch",
  targetsHeading: "Dein Ziel",
  targetsIntro: "Keine Badezimmerwaage. Keine Kalorienwährung. Nur Zahlen für deinen Tag.",
  targetsKcalEyebrow: "Kcal pro Tag",
  targetsDone: "Fertig",
  targetsEdit: "Bearbeiten",
  targetsSuggestedTdee: (kcal) => `Empfohlener Gesamtumsatz: ${kcal} kcal`,
  targetsTdeeMethod: "(Mifflin-St Jeor)",
  targetsUse: "Übernehmen",
  targetsEstimateDisclaimer: "Schätzwerte. Kein Ersatz für die Beratung durch eine Fachkraft.",
  targetsMacroEyebrow: "Makro-Verteilung · %",
  targetsMacroOk: "= 100",
  targetsMacroBad: (sum) => `= ${sum} (muss 100 sein)`,
  targetsMacroProtein: "EIWEISS",
  targetsMacroFat: "FETT",
  targetsMacroCarbs: "KOHLENHYDRATE",
  targetsAutoTdee: "Auto-Gesamtumsatz",
  targetsAutoTdeeValue: (kcal) => `${kcal} kcal · Mifflin-St Jeor`,
  targetsAutoTdeeHint: "4 Angaben → Kcal-Vorschlag",
  targetsDemographicsNote:
    "Diese Angaben sind optional, nur für die Formel. Sie werden anderen nicht angezeigt.",
  targetsSexLabel: "BIOLOGISCHES GESCHLECHT",
  targetsSexNone: "— keine Angabe —",
  targetsSexFemale: "weiblich",
  targetsSexMale: "männlich",
  targetsBirthYearLabel: "GEBURTSJAHR",
  targetsBirthYearPlaceholder: "z. B. 1990",
  targetsHeightLabel: "GRÖSSE (CM)",
  targetsHeightPlaceholder: "z. B. 178",
  targetsWeightLabel: "GEWICHT (KG)",
  targetsWeightPlaceholder: "z. B. 72",
  targetsActivityLabel: "AKTIVITÄTSLEVEL",
  targetsActivityNone: "— auswählen —",
  targetsActivitySedentary: "Sitzend (Bürojob, wenig Bewegung)",
  targetsActivityLight: "Leicht (Spaziergänge, 1-2× Sport/Woche)",
  targetsActivityModerate: "Mittel (3-5× Sport/Woche)",
  targetsActivityHigh: "Hoch (6-7× Sport, körperliche Arbeit)",
  targetsActivityVeryHigh: "Sehr hoch (tägliches hartes Training)",
  targetsUseTdee: (kcal) => `${kcal} kcal übernehmen`,
  targetsFillAll: "Fülle alle Felder aus, um eine Schätzung zu sehen.",
  targetsSafetyEyebrow: "SICHERHEIT",
  targetsSafetyBody:
    "Ein Ziel unter 1.200 kcal muss bestätigt werden. Hilfe: 0800 111 0 111.",
  targetsPctSumError: (sum) => `Die Prozente müssen 100 ergeben (aktuell ${sum})`,
  targetsGenericError: "Etwas ist schiefgelaufen",
  targetsSaving: "Speichere…",
  targetsSave: "Ziel speichern",
  targetsCancel: "Abbrechen",
  targetsSignOut: "Abmelden",

  sanityTitle: "Schauen wir gemeinsam",
  sanityBody: (kcal) =>
    `${kcal} kcal am Tag liegen unter dem medizinischen Minimum für die meisten Erwachsenen. Wenn eine Fachkraft dieses Ziel mit dir festgelegt hat, ist das in Ordnung. Wenn es deine eigene Idee ist, lohnt sich eine Beratung.`,
  sanitySupport:
    "Wenn dich Gedanken über Essen oder Gewicht belasten, sprich mit jemandem. Telefonseelsorge:",
  sanityConfirm: "Ich weiß, was ich tue",
  sanityCancel: "Zurück und korrigieren",

  crisisSupport:
    "Wenn dich Gedanken über Essen oder Gewicht belasten, sprich mit jemandem. Telefonseelsorge:",
  helplineLabel: "0800 111 0 111",
  helplineHref: "tel:08001110111",

  favTopTitle: "FAVORITEN",
  favBack: "Tagebuch",
  favEdit: "Bearbeiten",
  favDone: "Fertig",
  favHeading: "Favoriten",
  favCount: (n) => `${n} ${n === 1 ? "Eintrag" : "Einträge"}`,
  favTagline: "in einer Sekunde erfassen",
  favLoading: "Lädt…",
  favSearchPlaceholder: "Suchen…",
  favSortUseCount: "AM HÄUFIGSTEN",
  favSortLastUsed: "ZULETZT",
  favSortAlphabetical: "A–Z",
  favEmptyState:
    'Leer. Füge Favoriten hinzu, indem du bei einer Mahlzeit im Tracker auf "☆ Favorit" tippst.',
  favNoMatch: (query) => `Nichts passt zu „${query}".`,
  favRelNever: "NIE VERWENDET",
  favRelToday: "HEUTE",
  favRelYesterday: "GESTERN",
  favRelDays: (n) => `VOR ${n} ${n === 1 ? "TAG" : "TAGEN"}`,
  favRelWeekAgo: "VOR EINER WOCHE",
  favRelWeeks: (n) => `VOR ${n} WOCHEN`,
  favRelMonths: (n) => `VOR ${n} ${n === 1 ? "MONAT" : "MONATEN"}`,
  favDeleteConfirm: (name) => `"${name}" aus Favoriten entfernen?`,
  favDelete: "Löschen",
  favLog: "+ Erfassen",
  favDeleteAria: (name) => `${name} löschen`,
  favLoadFailed: "Laden fehlgeschlagen",
  favLogFailed: "Hinzufügen fehlgeschlagen",
  favDeleteFailed: "Löschen fehlgeschlagen",

  favSectionHeading: "Favoriten",
  favSectionSeeAll: (n) => `Alle anzeigen (${n})`,
  favSectionManage: "Verwalten",
  favSectionAdd: "Hinzufügen",
  favSectionDeleteAria: (name) => `${name} aus Favoriten entfernen`,

  favStripEyebrow: "★ Schnell erfassen",

  adminBack: "Zurück",
  adminEyebrow: "ADMIN-PANEL",
  adminHeading: "Sytofood Admin",
  adminNoAccess: "Kein Zugriff",
  adminLoadFailed: "Laden fehlgeschlagen",
  adminLoading: "Lädt...",
  adminKpiUsers: "NUTZER",
  adminKpiUsersSub: (verified) => `${verified} bestätigt`,
  adminKpiActive7d: "AKTIV 7T",
  adminKpiCostMonth: "KOSTEN MONAT",
  adminKpiCostMonthSub: (calls) => `${calls} Calls gesamt`,
  adminKpiCostTotal: "KOSTEN GESAMT",
  adminKpiCostTotalSub: (alerts) => `Warnungen: ${alerts} (30T)`,
  adminKpiReferrals: "EMPFEHLUNGEN",
  adminKpiReferralsSub: (active) => `${active} aktiv`,
  adminCostByModel: "KOSTEN NACH MODELL",
  adminNoData: "Keine Daten.",
  adminCalls: "CALLS",
  adminTopReferrers: "TOP-EMPFEHLENDE",
  adminTopSpenders: "TOP-AUSGABEN (DIESER MONAT)",
  adminAllUsers: (n) => `ALLE NUTZER (${n})`,
  adminSearchPlaceholder: "E-Mail / Name suchen...",
  adminSortCost: "Kosten",
  adminSortCalls: "Calls",
  adminSortInvites: "Eingeladen",
  adminSortCreated: "Erstellt",
  adminSortActive: "Aktiv",
  adminUnverifiedBadge: "⚠ UNBESTÄTIGT",
  adminUserMetaLast: "LETZTE",
  adminUserMetaCode: "CODE",
  adminUserMetaInvites: "EINGEL.",
  adminUserMetaFrom: "VON",
  adminPerMonth: "/MON",
  adminCloseAria: "Schließen",
  adminDetailCalls: "CALLS",
  adminDetailTotal: "GESAMT",
  adminDetailMonth: "MONAT",
  adminDetailReferral: "EMPFEHLUNG",
  adminDetailOwnCode: "Eigener Code",
  adminDetailNoCode: "keiner (Legacy)",
  adminDetailInvitedBy: "Eingeladen von",
  adminDetailRecentCalls: "LETZTE 100 CALLS",
  adminDetailNoCalls: "Keine Calls.",
  adminDetailClose: "Schließen",
};

export const appCopy: Record<Lang, AppCopy> = { pl, en, de };
